/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import DOMPurify from 'dompurify';
import styles from './Tooltip.module.scss';
import messages from './messages';
import { TOOLTIP_STYLES } from './constants';

/**
 * @param {{delay?: number, direction?: string, align?: string, content: string, reactNodesContent?: ReactNode, children?: ReactNode, style?: string}} props
 */
const Tooltip = ({
  delay = 400,
  direction = 'top',
  align = undefined,
  content,
  reactNodesContent = undefined,
  children,
  style = TOOLTIP_STYLES.DEFAULT,
}) => {
  const { sanitize } = DOMPurify;
  const { formatMessage } = useIntl();

  let timeout;
  const [active, setActive] = useState(false);

  const showTip = () => {
    timeout = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    clearInterval(timeout);
    setActive(false);
  };

  useEffect(() => () => clearInterval(timeout));

  return (
    <div
      data-testid="tooltip-wrapper"
      className={classnames(styles['tooltip-wrapper'], {
        [styles['tooltip-tip-style-square']]: style === TOOLTIP_STYLES.square,
      })}
    >
      <span
        onMouseEnter={showTip}
        onClick={showTip}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            showTip();
          }
        }}
        role="button"
        tabIndex="0"
        onMouseLeave={hideTip}
        className={styles['tooltip-title']}
      >
        {children}
      </span>
      {active && (
        <div
          className={classnames(
            styles['tooltip-tip'],
            styles[direction],
            styles[align],
          )}
        >
          <div
            className={styles['btn-close']}
            onClick={hideTip}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                hideTip();
              }
            }}
            role="button"
            aria-label={formatMessage(messages.closeTooltip)}
            tabIndex="0"
          />
          {reactNodesContent ? (
            <div
              className={classnames(
                styles['tooltip-content'],
                'tooltip-content',
                {
                  [styles['tooltip-content-padding-right']]:
                    style === TOOLTIP_STYLES.square,
                },
              )}
            >
              {reactNodesContent}
            </div>
          ) : (
            <div
              className={classnames(
                styles['tooltip-content'],
                'tooltip-content',
              )}
              dangerouslySetInnerHTML={{ __html: sanitize(content) }}
            />
          )}
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  delay: PropTypes.number,
  direction: PropTypes.oneOf(['top', 'bottom', 'right', 'left', 'right-edge']),
  align: PropTypes.oneOf(['top', 'bottom']),
  content: PropTypes.node.isRequired,
  reactNodesContent: PropTypes.node,
  children: PropTypes.node.isRequired,
  style: PropTypes.string,
};

export default Tooltip;
